/**
 * Nada se apaga en silencio.
 *
 * El defecto
 * ----------
 * El sistema hace lo correcto y no lo explica, así que la persona concluye que
 * está roto. Le pasó al dueño del proyecto —«¿por qué no puedo usar los modelos
 * de precio del cliente, venta rápida o lo que debe?»— y le pasó a quien probó
 * el manual. Los dos creyeron que funciones que SÍ andan estaban rotas.
 *
 * Relevados, son treinta y tres lugares donde el sistema niega algo. En
 * veintisiete no ofrecía ninguna salida. Y lo más caro: **la explicación ya
 * estaba escrita**. El backend calcula, para cada acción y cada reporte de cada
 * registro, un `disabled_reason` redactado término por término en castellano
 * —««Registrar cobro» no está disponible: «Estado» tiene que ser «Confirmada» y
 * es «Pagada»…»— y el frontend lo tiraba entero: estaba tipado en `lib/types.ts`
 * y no se leía en ninguna línea de la aplicación.
 *
 * Qué hace este archivo
 * ---------------------
 * Es **el** mecanismo, no una lista de casos. Tres piezas:
 *
 * 1. `ControlBloqueado` / `BotonBloqueado`: un control que no se puede usar
 *    ahora, que dice por qué **donde la persona lo busca** —al pasar el mouse, al
 *    enfocarlo con teclado y al tocarlo en una pantalla táctil— y que ofrece la
 *    salida si la hay.
 * 2. `AvisoDeBloqueo`: lo mismo cuando lo que falta no es un botón sino una
 *    sección entera (la grilla sin «Agregar línea», el panel que no se dibuja en
 *    un registro nuevo, el formulario en sólo lectura).
 * 3. `AccionesNoDisponibles`: la línea plegable de la cabecera que lista lo que
 *    el backend deshabilitó **con su motivo**. La decisión de no dibujar el botón
 *    muerto no cambia; lo que se termina es la desaparición sin rastro.
 *
 * Tres reglas duras
 * -----------------
 * * **No cambia el comportamiento, cambia la explicación.** Lo que hoy no puede
 *   correr sigue sin poder correr. `ControlBloqueado` nunca ejecuta lo que
 *   envuelve.
 * * **El motivo sale del servidor.** Salvo las frases que hablan de la PANTALLA
 *   —«hay cambios sin guardar»—, que son de la pantalla y de nadie más, el texto
 *   es el `disabled_reason` que ya viaja por el cable. El frontend no escribe
 *   reglas de negocio.
 * * **Nada emite ni factura solo.** La salida «Guardar y seguir» guarda, y sólo
 *   encadena la acción cuando la acción **no escribe nada** (`readonly` en la
 *   metadata: es un cálculo o una consulta). Para todo lo demás guarda, deja el
 *   botón habilitado y con el foco, y la aprieta la persona. Ver
 *   `SalidaDeBloqueo.encadena`.
 *
 * Por qué `aria-disabled` y no `disabled`
 * ---------------------------------------
 * Un `<button disabled>` no recibe foco y no emite eventos. Eso hacía que el
 * único canal posible fuera el `title` nativo: tooltip con un segundo de demora,
 * sobre un control muerto que nadie tiene motivo para hoverear, sin equivalente
 * con teclado ni en táctil y no anunciado por lector de pantalla. Con
 * `aria-disabled` el botón sigue enfocable y sigue emitiendo `click`: se lo
 * anuncia como no disponible, se lo describe con `aria-describedby` y al
 * apretarlo cuenta el motivo. La acción sigue sin ejecutarse.
 */

import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { IconAlert, IconChevronDown } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';

// ---------------------------------------------------------------------------
// El contrato
// ---------------------------------------------------------------------------
/**
 * Lo que la persona puede hacer AHORA para destrabar el control.
 *
 * Existe sólo cuando la salida es obvia y está a un clic. Cuando lo que falta es
 * que la factura cambie de estado no hay salida y no se inventa una: se dice el
 * motivo y listo.
 */
export interface SalidaDeBloqueo {
  /** Lo que dice el botón del globo. */
  etiqueta: string;
  /** Qué va a pasar exactamente. Se lee ANTES de apretar, no después. */
  aclaracion: string;
  /** Lo ejecuta el botón del globo. Nunca corre solo. */
  ejecutar: () => void;
  /** `true` mientras está corriendo. */
  corriendo?: boolean;
}

/** Un control apagado: por qué, y qué se puede hacer al respecto. */
export interface Bloqueo {
  motivo: string;
  salida?: SalidaDeBloqueo | null;
}

// ---------------------------------------------------------------------------
// Guardado: el contexto que le da la salida a toda la ficha
// ---------------------------------------------------------------------------
/**
 * Lo que el formulario le presta a sus descendientes para que puedan ofrecer
 * «guardar» sin recibirlo por prop a través de seis niveles.
 *
 * Se usa donde la única traba es que el registro no está guardado: la grilla de
 * renglones de un alta, el panel de venta rápida, los adjuntos, el seguimiento,
 * los botones de la cabecera. **El mismo mecanismo y el mismo texto en todos**,
 * que es lo que quiere decir «que el sistema hable siempre igual».
 */
export interface EstadoDeGuardado {
  /** `true` si el registro todavía no existe en la base. */
  esNuevo: boolean;
  /** `true` si hay campos tocados sin guardar. */
  haySucios: boolean;
  /** Está guardando ahora mismo. */
  guardando: boolean;
  /**
   * Guarda el registro.
   *
   * * `continuar` corre DESPUÉS de que el guardado terminó bien, y sólo se pasa
   *   cuando encadenar es seguro: ver `SalidaDeBloqueo`. El formulario valida
   *   igual que siempre y, si falta un dato, enfoca el campo y `continuar` no
   *   corre.
   * * `devolverFocoA` es la clave del botón que quedó destrabado. El formulario
   *   le pone el foco cuando vuelve el guardado: eso es lo que hace que
   *   «guardar y soltar» no deje a quien navega con teclado en la nada,
   *   buscando a tientas el botón que acaba de revivir.
   */
  guardar: (opciones?: { continuar?: (() => void) | null; devolverFocoA?: string | null }) => void;
  /**
   * Deja anotado algo para correr DESPUÉS del próximo guardado que salga bien,
   * con el registro ya guardado en la mano. Devuelve cómo desanotarlo.
   *
   * Es la hermana de `guardar({continuar})` para el caso que aquélla no cubre:
   * ahí es la PERSONA la que aprieta «Guardar y seguir» sobre un control
   * bloqueado, y por eso en un alta no encadena nada (la pantalla se está
   * reemplazando por la del registro recién creado). Acá el que anota es un
   * CONTROL del formulario que necesita el id para terminar lo que el usuario
   * ya eligió —el desplegable «¿Qué es este contacto?» eligiendo «Un
   * administrador de consorcios»—, y el alta es justamente su caso principal.
   *
   * Las tres reglas que la mantienen inofensiva:
   *
   * * **Se anota, no se ejecuta.** Corre una sola vez y sólo si el guardado
   *   volvió bien; un guardado que falla la descarta.
   * * **Muere con la ficha.** La lista vive en el formulario, así que nada
   *   puede quedar colgado para el registro siguiente.
   * * **La anota quien la va a correr**, y la desanota al soltar: un control
   *   que cambia de idea (el usuario vuelve a «Una persona») saca la suya.
   *
   * Opcional a propósito: los formularios de prueba y cualquier otro proveedor
   * de este contexto siguen siendo válidos sin implementarla, y un control que
   * la use tiene que degradar sin romper cuando no está.
   */
  alGuardar?: (accion: (registro: { id: number }) => void) => () => void;
}

const ContextoDeGuardado = createContext<EstadoDeGuardado | null>(null);

export function ProveedorDeGuardado({
  estado,
  children,
}: {
  estado: EstadoDeGuardado;
  children: ReactNode;
}) {
  return <ContextoDeGuardado.Provider value={estado}>{children}</ContextoDeGuardado.Provider>;
}

/**
 * El guardado de la ficha, o `null` si esto no se está dibujando dentro de una.
 *
 * Degrada a nada a propósito: la misma grilla se usa en pantallas que no son un
 * formulario, y ahí simplemente no se ofrece la salida.
 */
export function useGuardadoDeLaFicha(): EstadoDeGuardado | null {
  return useContext(ContextoDeGuardado);
}

/** Lo que se lee cuando falta guardar. Uno solo, para los dos lados del sistema. */
export const MOTIVO_SUCIO = 'Guardá los cambios para poder usar esta acción.';
export const MOTIVO_SUCIO_IMPRIMIR = 'Guardá los cambios para poder imprimir.';
export const MOTIVO_SUCIO_MAIL = 'Guardá los cambios para poder enviarlo por email.';

/** El detalle que va debajo del motivo cuando la traba son cambios pendientes. */
export const DETALLE_SUCIO =
  'Esta acción trabaja sobre el registro guardado en el servidor, no sobre lo que ves en pantalla: con cambios pendientes te daría un resultado que no coincide.';

/**
 * Arma la salida «guardar» a partir del contexto de la ficha.
 *
 * `encadena` es la decisión delicada y por eso es un parámetro explícito y no un
 * default: sólo va en `true` cuando lo que sigue **no escribe nada** (una
 * consulta, un cálculo, un reporte). Un guardado que encadena una emisión es
 * exactamente lo que este trabajo no puede introducir.
 */
export function salidaDeGuardado(
  guardado: EstadoDeGuardado | null,
  opciones: { continuar?: (() => void) | null; encadena: boolean; clave?: string },
): SalidaDeBloqueo | null {
  if (guardado === null) return null;
  const { continuar = null, encadena, clave = null } = opciones;
  return {
    etiqueta: encadena ? 'Guardar y seguir' : 'Guardar',
    aclaracion: encadena
      ? 'Se guarda el registro y se ejecuta a continuación. Esta acción sólo consulta: no emite ni factura nada.'
      : 'Se guarda el registro y el botón queda habilitado, con el foco puesto. La acción la apretás vos: no encadenamos un guardado con algo que emite.',
    ejecutar: () => {
      guardado.guardar({
        continuar: encadena ? continuar : null,
        devolverFocoA: encadena ? null : clave,
      });
    },
    ...(guardado.guardando ? { corriendo: true } : {}),
  };
}

// ---------------------------------------------------------------------------
// El globo
// ---------------------------------------------------------------------------
/**
 * Dónde se dibuja el globo respecto del control.
 *
 * `debajo` es el de siempre y sirve en una fila de botones. `al-costado` existe
 * para lo que vive DENTRO de un desplegable: ahí el globo que baja se le monta
 * encima a las opciones que sí funcionan —«Archivar», «Eliminar»— y tapa
 * justamente lo único que la persona podía apretar.
 */
export type PosicionDelGlobo = 'debajo' | 'al-costado';

/**
 * El motivo dibujado, con su salida si la hay.
 *
 * `aria-hidden` en el texto porque el mismo texto ya viaja por `aria-describedby`
 * desde una copia permanente: sin eso el lector de pantalla lo diría dos veces.
 * El botón de la salida NO va oculto: es un control de verdad y se anuncia como
 * tal.
 */
function Globo({
  motivo,
  salida,
  id,
  posicion,
}: {
  motivo: string;
  salida: SalidaDeBloqueo | null;
  id: string;
  posicion: PosicionDelGlobo;
}) {
  return (
    <span
      // `absolute` + `z-50`: la cabecera del formulario tiene los botones al ras
      // del borde derecho y el globo se sale de la fila.
      className={[
        'absolute z-50 block w-72 max-w-[min(18rem,90vw)] rounded-lg border border-line bg-surface p-3 text-left shadow-lg',
        posicion === 'al-costado' ? 'right-full top-0 mr-2' : 'right-0 top-full mt-1',
      ].join(' ')}
    >
      <span aria-hidden="true" className="block text-sm text-fg">
        {motivo}
      </span>
      {salida !== null && (
        <span className="mt-2 block">
          <span aria-hidden="true" className="mb-2 block text-xs text-fg-faint">
            {salida.aclaracion}
          </span>
          <Button
            size="sm"
            variant="primary"
            loading={salida.corriendo === true}
            aria-describedby={id}
            onClick={(evento) => {
              evento.preventDefault();
              evento.stopPropagation();
              salida.ejecutar();
            }}
          >
            {salida.etiqueta}
          </Button>
        </span>
      )}
    </span>
  );
}

export interface ControlBloqueadoProps extends Bloqueo {
  /**
   * El control, ya en estado bloqueado. Recibe lo que tiene que ponerle al
   * elemento: `aria-describedby` y el `onClick` que abre el globo en vez de
   * ejecutar.
   */
  children: (props: {
    'aria-describedby': string;
    onClick: (evento: { preventDefault: () => void; stopPropagation: () => void }) => void;
  }) => ReactNode;
  className?: string;
  /** Ver `PosicionDelGlobo`. Por defecto, debajo del control. */
  posicion?: PosicionDelGlobo;
}

/**
 * Envuelve un control apagado y le pone el motivo encima.
 *
 * Se abre de las tres maneras y por eso son tres, no una:
 *
 * * **mouse**: al pasar por encima (`onMouseEnter`), sin esperar el segundo del
 *   tooltip nativo;
 * * **teclado**: al recibir el foco (`onFocusCapture`), que es la única forma de
 *   que exista para quien no usa mouse — y como el globo vive DENTRO del mismo
 *   contenedor, tabular una vez más lleva al botón de la salida;
 * * **táctil y clic**: al apretar el control, que lo deja fijo hasta que se
 *   apriete Escape o se toque afuera. Ahí es donde aparece «Hay cambios sin
 *   guardar. ¿Guardar y seguir?» sin que nadie tenga que adivinar que hay algo
 *   que hoverear.
 *
 * El motivo está SIEMPRE en el DOM en una copia para lector de pantalla, esté el
 * globo visible o no: `aria-describedby` tiene que apuntar a algo que exista.
 */
export function ControlBloqueado({
  motivo,
  salida = null,
  children,
  className,
  posicion = 'debajo',
}: ControlBloqueadoProps) {
  const [encima, setEncima] = useState(false);
  const [fijo, setFijo] = useState(false);
  const contenedor = useRef<HTMLSpanElement>(null);
  const id = `motivo-${useId()}`;

  useEffect(() => {
    if (!fijo) return;
    const afuera = (evento: MouseEvent): void => {
      if (contenedor.current !== null && !contenedor.current.contains(evento.target as Node)) {
        setFijo(false);
      }
    };
    const teclas = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') setFijo(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', teclas);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', teclas);
    };
  }, [fijo]);

  const visible = encima || fijo;

  return (
    <span
      ref={contenedor}
      className={['relative inline-flex', className ?? ''].join(' ').trim()}
      onMouseEnter={() => {
        setEncima(true);
      }}
      onMouseLeave={() => {
        setEncima(false);
      }}
      onFocusCapture={() => {
        setEncima(true);
      }}
      onBlurCapture={(evento) => {
        // Sólo se cierra cuando el foco se fue del conjunto: si pasó al botón de
        // la salida —que está adentro— el globo tiene que seguir abierto.
        if (!contenedor.current?.contains(evento.relatedTarget as Node | null)) setEncima(false);
      }}
    >
      {children({
        'aria-describedby': id,
        onClick: (evento) => {
          evento.preventDefault();
          evento.stopPropagation();
          setFijo((actual) => !actual);
        },
      })}

      {/* La copia permanente para el lector de pantalla. Es la que describe al
          control; el globo de abajo es su dibujo. */}
      <span id={id} className="sr-only">
        {motivo}
        {salida !== null ? ` ${salida.aclaracion}` : ''}
      </span>

      {visible && <Globo motivo={motivo} salida={salida} id={id} posicion={posicion} />}
    </span>
  );
}

/**
 * El caso de siempre: un botón apagado.
 *
 * Se usa en todos lados donde antes había un `<Button disabled title="…">`.
 */
export function BotonBloqueado({
  motivo,
  salida = null,
  label,
  variant = 'secondary',
  size = 'md',
  className,
}: Bloqueo & {
  label: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <ControlBloqueado motivo={motivo} salida={salida} {...(className === undefined ? {} : { className })}>
      {(props) => (
        <Button blocked size={size} variant={variant} {...props}>
          {label}
        </Button>
      )}
    </ControlBloqueado>
  );
}

// ---------------------------------------------------------------------------
// Cuando lo que falta no es un botón sino una sección
// ---------------------------------------------------------------------------
/**
 * El cartel que explica por qué una parte de la pantalla no se puede usar.
 *
 * Va **en el lugar donde la persona la buscaba** y no arriba de todo con los
 * demás carteles: el hueco que quedaba era justamente de ubicación —el motivo
 * del borrado bloqueado se dibuja a media pantalla del botón que no está—.
 *
 * `role="status"` y no `role="alert"`: no pasó nada malo, es información sobre
 * el estado de la pantalla, y un `alert` interrumpe la lectura.
 */
export function AvisoDeBloqueo({
  motivo,
  salida = null,
  tono = 'neutro',
  className,
}: Bloqueo & { tono?: 'neutro' | 'atencion'; className?: string }) {
  return (
    <div
      role="status"
      className={[
        'flex flex-col gap-2 rounded-lg border px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between',
        tono === 'atencion'
          ? 'border-warning/30 bg-warning-soft text-fg'
          : 'border-dashed border-line bg-app-subtle text-fg-muted',
        className ?? '',
      ]
        .join(' ')
        .trim()}
    >
      <span className="min-w-0">
        <span className="block">{motivo}</span>
        {salida !== null && <span className="mt-0.5 block text-xs text-fg-faint">{salida.aclaracion}</span>}
      </span>
      {salida !== null && (
        <Button
          size="sm"
          variant="primary"
          className="shrink-0"
          loading={salida.corriendo === true}
          onClick={salida.ejecutar}
        >
          {salida.etiqueta}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lo que el servidor deshabilitó
// ---------------------------------------------------------------------------
/** Una acción o un reporte que hoy no se puede, con el motivo del servidor. */
export interface AccionNoDisponible {
  clave: string;
  etiqueta: string;
  motivo: string;
}

/**
 * «N acciones no disponibles ahora», plegable, siempre en el mismo lugar.
 *
 * El caso que cierra, medido en el sistema andando: sobre el comprobante 255 la
 * API devuelve 7 de 9 acciones deshabilitadas **con sus motivos completos** y la
 * cabecera dibujaba 2 botones y cero explicaciones. Quien buscaba «Registrar
 * cobro» no tenía forma de saber que existía ni por qué no estaba. Sobre la
 * cuenta contable de agrupación desaparecía «Ver mayor» sin un solo cartel, y
 * ese es el peor de todos: no habla de estados, así que la persona salía a
 * buscar un problema de estado que no existía.
 *
 * **La decisión de no dibujar el botón muerto no cambia.** Un botón que no se va
 * a poder apretar sigue siendo ruido. Lo que cambia es que ahora hay dónde
 * enterarse, en una línea que ocupa una línea.
 */
export function AccionesNoDisponibles({ acciones }: { acciones: AccionNoDisponible[] }) {
  const [abierto, setAbierto] = useState(false);
  const id = `no-disponibles-${useId()}`;

  if (acciones.length === 0) return null;

  return (
    <div className="mb-4">
      <button
        type="button"
        aria-expanded={abierto}
        aria-controls={id}
        onClick={() => {
          setAbierto((actual) => !actual);
        }}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-sm text-fg-muted transition-colors hover:text-fg"
      >
        <IconChevronDown
          className={['h-4 w-4 transition-transform', abierto ? '' : '-rotate-90'].join(' ')}
          aria-hidden="true"
        />
        {/* «no disponible» es la palabra de programador que encabezaba los 66
            motivos de bloqueo del sistema, y también este título. Nadie dice
            «no disponible» cuando le explica algo a otra persona. */}
        {acciones.length === 1
          ? '1 acción que todavía no se puede'
          : `${acciones.length} acciones que todavía no se pueden`}
      </button>

      {abierto && (
        <ul id={id} className="mt-2 space-y-1.5 rounded-lg border border-line bg-app-subtle px-3 py-2.5">
          {acciones.map((accion) => (
            <li key={accion.clave} className="flex gap-2 text-sm">
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0 text-fg-faint" aria-hidden="true" />
              <span className="min-w-0">
                <span className="font-medium text-fg">{accion.etiqueta}</span>
                <span className="mt-0.5 block text-fg-muted">{accion.motivo}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * El motivo que manda el servidor, o una frase honesta si no mandó ninguno.
 *
 * La frase de reserva no dice «no está disponible en el estado actual» —esa es
 * la que miente cuando la condición no habla de estados— sino que se limita a lo
 * que se sabe con certeza.
 */
export function motivoDelServidor(razon: string | null | undefined, etiqueta: string): string {
  const texto = typeof razon === 'string' ? razon.trim() : '';
  return texto !== '' ? texto : `«${etiqueta}» no se puede usar sobre este registro.`;
}
